import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { FraudWarningsService, FraudWarningItem } from '../../services/fraud-warnings.service';

interface Column {
  field: string;
  header: string;
}

@Component({
  selector: 'app-fraud-warnings',
  imports: [TableModule, TagModule, CommonModule, BreadcrumbModule, ButtonModule, DialogModule, RouterLink, TooltipModule, ToggleSwitchModule, FormsModule],
  templateUrl: './fraud-warnings.html',
  styleUrl: './fraud-warnings.scss'
})
export class FraudWarningsComponent implements OnInit {
  public service = inject(FraudWarningsService);
  protected home: MenuItem = { label: 'Betrugswarnungen' };
  public cols!: Column[];
  public selectedWarning: FraudWarningItem | null = null;
  public showDetailsDialog = false;
  public showAcknowledged = false;

  ngOnInit() {
    this.cols = [
      { field: 'created', header: 'Gemeldet' },
      { field: 'fallaufgabenName', header: 'Fallaufgabe' },
      { field: 'userId', header: 'Nutzer ID' },
      { field: 'fraudWarnings', header: 'Warnungen' },
      { field: 'startDate', header: 'Start' },
      { field: 'endDate', header: 'Ende' }
    ];
  }

  public openDetails(warning?: FraudWarningItem | null) {
    if (!warning) return;
    this.selectedWarning = warning;
    this.showDetailsDialog = true;
  }

  public getWarningCount(warning?: FraudWarningItem | null): number {
    return warning?.fraudWarnings?.length ?? 0;
  }

  public getFirstWarningIp(warning?: FraudWarningItem | null): string {
    return warning?.fraudWarnings?.[0]?.ip ?? 'N/A';
  }

  public getWarningTypeSummary(item?: FraudWarningItem | null): string {
    if (!item?.fraudWarnings?.length) return 'Keine Warnungen';
    const counts = new Map<string, number>();
    for (const w of item.fraudWarnings) {
      counts.set(this.getWarningLabel(w.type), (counts.get(this.getWarningLabel(w.type)) ?? 0) + 1);
    }
    return [...counts.entries()].map(([type, count]) => `${type}: ${count}`).join('\n');
  }

  public getWarningLabel(type: string): string {
    switch (type) {
      case 'same-fallaufgabe-same-ip-60m-different-user': return 'Gleiche IP, anderer Nutzer';
      case 'similar-answers-high': return 'Sehr ähnliche Antworten';
      case 'similar-answers-medium': return 'Ähnliche Antworten';
      case 'similar-answers-low': return 'Leicht ähnliche Antworten';
      case 'copy-paste-detected': return 'Auffälliges Paste-Verhalten';
      case 'suspicious-copy-behavior': return 'Auffälliges Kopierverhalten';
      case 'llm-content-detected-high': return 'Hohe KI-Wahrscheinlichkeit';
      case 'llm-content-detected-medium': return 'Mittlere KI-Wahrscheinlichkeit';
      case 'llm-content-detected-low': return 'Niedrige KI-Wahrscheinlichkeit';
      default: return type;
    }
  }

  public getWarningSeverity(type: string): 'danger' | 'warn' | 'info' | 'secondary' {
    switch (type) {
      case 'suspicious-copy-behavior': return 'danger';
      case 'similar-answers-high': return 'danger';
      case 'similar-answers-medium': return 'warn';
      case 'similar-answers-low': return 'info';
      case 'llm-content-detected-high': return 'danger';
      case 'llm-content-detected-medium': return 'warn';
      case 'llm-content-detected-low': return 'info';
      default: return 'secondary';
    }
  }

  public getCopyRatioPercent(warning: FraudWarningItem['fraudWarnings'][number]): number | null {
    const ratio = warning.copyStats?.ratio;
    if (ratio == null) return null;
    return Math.round(ratio * 100);
  }

  public get filteredItems(): FraudWarningItem[] {
    if (this.showAcknowledged) return this.service.items;
    return this.service.items.filter((item) => !item.acknowledged);
  }

  public async acknowledge(item: FraudWarningItem) {
    await this.service.acknowledge(item.statusId);
  }

  public async unacknowledge(item: FraudWarningItem) {
    await this.service.unacknowledge(item.statusId);
  }
}
